import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from './lib/supabase';
import { PayPalButtons, usePayPalScriptReducer } from '@paypal/react-paypal-js';
import { getPayPalError } from './utils/paypalErrorHandler';
import './BookSession.css';

function BookSpecialist() {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [specialist, setSpecialist] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [userNotes, setUserNotes] = useState('');
  const [bookedSlots, setBookedSlots] = useState([]);
  const [isCheckingAvailability, setIsCheckingAvailability] = useState(false);
  const [availableTimeSlots, setAvailableTimeSlots] = useState([]);
  const [isLoadingAvailability, setIsLoadingAvailability] = useState(false);
  
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [showPayPal, setShowPayPal] = useState(false);
  const [paymentApproved, setPaymentApproved] = useState(false);
  const [{ isPending, isResolved, isRejected }] = usePayPalScriptReducer();

  const isLoggedIn = localStorage.getItem('userLoggedIn') === 'true';
  const userId = localStorage.getItem('userId');
  const userEmail = localStorage.getItem('userEmail');
  const userName = localStorage.getItem('userName');

  // Default consultation price for specialists (can be customized per specialist later)
  const consultationPrice = '€100';

  useEffect(() => {
    fetchSpecialistDetails();
  }, [id]);

  useEffect(() => {
    if (isLoggedIn) {
      setCustomerName(userName || '');
      setCustomerEmail(userEmail || '');
    }
  }, [isLoggedIn, userName, userEmail]);

  useEffect(() => {
    if (selectedDate) {
      fetchAdminAvailability();
      checkAvailability();
    } else {
      setAvailableTimeSlots([]);
      setSelectedTime(null);
    }
  }, [selectedDate, id]);

  const fetchSpecialistDetails = async () => {
    try {
      setIsLoading(true);
      const { data, error: fetchError } = await supabase
        .from('specialists')
        .select('*')
        .eq('id', id)
        .single();

      if (fetchError) {
        throw fetchError;
      }

      if (data) {
        setSpecialist(data);
      } else {
        setError('Specialist not found');
      }
    } catch (err) {
      console.error('Error fetching specialist:', err);
      setError('Failed to load specialist details. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAdminAvailability = async () => {
    if (!selectedDate) return;
    
    try {
      setIsLoadingAvailability(true);
      const dateString = selectedDate.toISOString().split('T')[0];
      const dayOfWeek = selectedDate.getDay();

      const [weeklyResult, dateResult] = await Promise.all([
        supabase.from('admin_weekly_schedule').select('*').eq('is_active', true),
        supabase.from('admin_date_availability').select('*')
      ]);

      const weeklySchedules = weeklyResult.data || [];
      const dateOverrides = dateResult.data || [];

      let timeSlots = [];

      const dateOverride = dateOverrides.find(d => d.availability_date === dateString);
      if (dateOverride && dateOverride.is_available) {
        timeSlots = dateOverride.time_slots || [];
      } else {
        const weeklySchedule = weeklySchedules.find(s => s.day_of_week === dayOfWeek);
        if (weeklySchedule) {
          timeSlots = weeklySchedule.time_slots || [];
        }
      }

      setAvailableTimeSlots(timeSlots.map(slot => slot.start || slot));
    } catch (err) {
      console.error('Error fetching availability:', err);
    } finally {
      setIsLoadingAvailability(false);
    }
  };

  const checkAvailability = async () => {
    if (!selectedDate || !id) return;
    
    try {
      setIsCheckingAvailability(true);
      const dateString = selectedDate.toISOString().split('T')[0];
      
      const { data, error } = await supabase
        .from('bookings')
        .select('booking_time')
        .eq('specialist_id', id)
        .eq('booking_date', dateString)
        .in('status', ['pending', 'confirmed']);

      if (error) {
        console.error('Error checking availability:', error);
        return;
      }

      const bookedTimes = (data || []).map(booking => booking.booking_time);
      setBookedSlots(bookedTimes);
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setIsCheckingAvailability(false);
    }
  };

  const [availableDates, setAvailableDates] = useState([]);

  useEffect(() => {
    const loadAvailableDates = async () => {
      try {
        const dates = [];
        const today = new Date();
        
        const [weeklyResult, dateResult] = await Promise.all([
          supabase.from('admin_weekly_schedule').select('*').eq('is_active', true),
          supabase.from('admin_date_availability').select('*')
        ]);

        const weeklySchedules = weeklyResult.data || [];
        const dateOverrides = dateResult.data || [];

        const weeklyScheduleMap = {};
        weeklySchedules.forEach(schedule => {
          weeklyScheduleMap[schedule.day_of_week] = schedule;
        });

        const dateOverrideMap = {};
        dateOverrides.forEach(override => {
          dateOverrideMap[override.availability_date] = override;
        });

        for (let i = 1; i <= 30; i++) {
          const date = new Date(today);
          date.setDate(today.getDate() + i);
          const dateString = date.toISOString().split('T')[0];
          const dayOfWeek = date.getDay();

          let isAvailable = false;

          if (dateOverrideMap[dateString]) {
            isAvailable = dateOverrideMap[dateString].is_available && 
                         dateOverrideMap[dateString].time_slots?.length > 0;
          } else {
            const weeklySchedule = weeklyScheduleMap[dayOfWeek];
            isAvailable = weeklySchedule && 
                         weeklySchedule.time_slots?.length > 0;
          }

          if (isAvailable) {
            dates.push(date);
          }
        }

        setAvailableDates(dates);
      } catch (err) {
        console.error('Error loading available dates:', err);
        const dates = [];
        const today = new Date();
        for (let i = 1; i <= 30; i++) {
          const date = new Date(today);
          date.setDate(today.getDate() + i);
          dates.push(date);
        }
        setAvailableDates(dates);
      }
    };

    loadAvailableDates();
  }, []);

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const isFormValid = selectedDate && selectedTime && customerName.trim() && customerEmail.trim() && customerPhone.trim();

  const extractPrice = (priceString) => {
    if (!priceString) return 0;
    const numericString = priceString.replace(/[^0-9.]/g, '');
    return parseFloat(numericString) || 0;
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    setError('');
    
    if (!isFormValid) {
      setError('Please fill in all required fields');
      return;
    }

    if (bookedSlots.includes(selectedTime)) {
      setError('This time slot has already been booked. Please select another time.');
      return;
    }

    setShowPayPal(true);
  };

  const createBooking = async (paymentId = null, paymentStatus = 'pending') => {
    try {
      setIsSubmitting(true);
      const dateString = selectedDate.toISOString().split('T')[0];

      const bookingData = {
        user_id: userId || null,
        specialist_id: id,
        booking_date: dateString,
        booking_time: selectedTime,
        user_notes: userNotes,
        customer_name: customerName.trim(),
        customer_email: customerEmail.trim(),
        customer_phone: customerPhone.trim(),
        specialist_name: specialist.name,
        specialist_title: specialist.title || null,
        status: paymentStatus === 'completed' ? 'confirmed' : 'pending',
        payment_id: paymentId,
        payment_status: paymentStatus
      };

      const { data, error: insertError } = await supabase
        .from('bookings')
        .insert([bookingData])
        .select()
        .single();

      if (insertError) {
        throw insertError;
      }

      navigate('/booking-confirmation', { 
        state: {
          specialist_name: specialist.name,
          specialist_title: specialist.title,
          booking_date: dateString,
          date: formatDate(selectedDate),
          booking_time: selectedTime,
          time: selectedTime,
          price: consultationPrice,
          customer_name: customerName.trim(),
          customer_email: customerEmail.trim(),
          customer_phone: customerPhone.trim(),
          user_notes: userNotes,
          bookingId: data.id,
          paymentId: paymentId,
          paymentStatus: paymentStatus,
          isSpecialist: true
        }
      });
    } catch (err) {
      console.error('Error creating booking:', err);
      setError(err.message || 'Failed to create booking. Please try again.');
      setIsSubmitting(false);
    }
  };

  const handlePaymentApprove = async (data, actions) => {
    try {
      const order = await actions.order.capture();
      setPaymentApproved(true);
      await createBooking(order.id, 'completed');
    } catch (err) {
      console.error('Payment error:', err);
      setError('Payment processing failed. Please try again.');
      setPaymentApproved(false);
      setIsSubmitting(false);
    }
  };

  const handlePaymentError = (err) => {
    console.error('PayPal error:', err);
    const { message, keepPayPalVisible } = getPayPalError(err);
    setError(message);
    if (!keepPayPalVisible) setShowPayPal(false);
  };

  const handlePaymentCancel = () => {
    setShowPayPal(false);
    setError('Payment was cancelled. Please try again when ready.');
  };

  if (isLoading) {
    return (
      <div className="book-session-page">
        <div className="container" style={{ padding: '4rem 1rem', textAlign: 'center' }}>
          <div className="loading-spinner" style={{ 
            width: '50px', 
            height: '50px', 
            border: '4px solid rgba(124, 58, 237, 0.3)',
            borderTopColor: '#7C3AED',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 1rem'
          }}></div>
          <p>Loading specialist details...</p>
        </div>
      </div>
    );
  }

  if (error && !specialist) {
    return (
      <div className="book-session-page">
        <div className="container" style={{ padding: '4rem 1rem', textAlign: 'center' }}>
          <p style={{ color: '#ef4444', fontSize: '1.2rem' }}>{error}</p>
          <button 
            onClick={() => navigate('/services')}
            style={{
              marginTop: '1rem',
              padding: '0.75rem 1.5rem',
              background: '#7C3AED',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer'
            }}
          >
            Back to Services
          </button>
        </div>
      </div>
    );
  }

  if (!specialist) return null;

  return (
    <div className="book-session-page">
      <section className="session-overview">
        <div className="container">
          <div className="overview-content">
            <h1 className="session-service-name">{specialist.name}</h1>
            {specialist.title && (
              <div className="session-type-label">{specialist.title}</div>
            )}
            <div className="session-meta">
              {specialist.specialization && (
                <span className="meta-item">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 2L2 7L12 12L22 7L12 2Z" fill="currentColor"/>
                  </svg>
                  {specialist.specialization}
                </span>
              )}
              <span className="meta-item">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM12 17C11.45 17 11 16.55 11 16C11 15.45 11.45 15 12 15C12.55 15 13 15.45 13 16C13 16.55 12.55 17 12 17ZM13 13H11V7H13V13Z" fill="currentColor"/>
                </svg>
                {consultationPrice}
              </span>
            </div>
          </div>
        </div>
        <div className="wave-divider-overview"></div>
      </section>

      <form className="booking-form" onSubmit={handleFormSubmit}>
        <section className="booking-details-section">
          <div className="container">
            <h2 className="section-title">Select Date & Time</h2>
            
            <div className="calendar-section">
              <div className="calendar-grid">
                {availableDates.map((date, index) => {
                  const isSelected = selectedDate && 
                    date.toDateString() === selectedDate.toDateString();
                  return (
                    <button
                      key={index}
                      type="button"
                      className={`date-button ${isSelected ? 'selected' : ''}`}
                      onClick={() => setSelectedDate(date)}
                    >
                      <span className="date-day">{date.getDate()}</span>
                      <span className="date-month">{date.toLocaleDateString('en-US', { month: 'short' })}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {selectedDate && (
              <div className="time-slots-section">
                <h3 className="time-slots-title">
                  Available Times
                  {(isCheckingAvailability || isLoadingAvailability) && (
                    <span style={{ fontSize: '0.9rem', marginLeft: '1rem', opacity: 0.7 }}>
                      Loading availability...
                    </span>
                  )}
                </h3>
                {isLoadingAvailability ? (
                  <div style={{ textAlign: 'center', padding: '2rem', color: '#C7D2FE' }}>
                    Loading available times...
                  </div>
                ) : availableTimeSlots.length === 0 ? (
                  <div style={{ 
                    textAlign: 'center', 
                    padding: '2rem', 
                    color: '#C7D2FE',
                    background: 'rgba(255, 255, 255, 0.05)',
                    borderRadius: '12px',
                    border: '1px solid rgba(255, 255, 255, 0.1)'
                  }}>
                    <p>No available time slots for this date. Please select another date.</p>
                  </div>
                ) : (
                  <div className="time-slots-grid">
                    {availableTimeSlots.map((time, index) => {
                      const isSelected = selectedTime === time;
                      const isBooked = bookedSlots.includes(time);
                      return (
                        <button
                          key={index}
                          type="button"
                          className={`time-slot-button ${isSelected ? 'selected' : ''} ${isBooked ? 'booked' : ''}`}
                          onClick={() => !isBooked && setSelectedTime(time)}
                          disabled={isBooked}
                          title={isBooked ? 'This time slot is already booked' : ''}
                        >
                          {time}
                          {isBooked && <span className="booked-badge">Booked</span>}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        </section>

        <section className="customer-info-section">
          <div className="container">
            <h2 className="section-title">Your Contact Information</h2>
            {error && (
              <div className="error-message" style={{ 
                background: 'rgba(239, 68, 68, 0.2)', 
                border: '1px solid #ef4444', 
                color: '#fff',
                padding: '1rem',
                borderRadius: '8px',
                marginBottom: '1.5rem'
              }}>
                {error}
              </div>
            )}
            <div className="customer-form-grid">
              <div className="form-group">
                <label htmlFor="customerName" className="form-label">
                  Full Name <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="customerName"
                  className="form-input"
                  placeholder="Enter your full name"
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="customerEmail" className="form-label">
                  Email <span className="required">*</span>
                </label>
                <input
                  type="email"
                  id="customerEmail"
                  className="form-input"
                  placeholder="Enter your email address"
                  value={customerEmail}
                  onChange={(e) => setCustomerEmail(e.target.value)}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="customerPhone" className="form-label">
                  Phone Number <span className="required">*</span>
                </label>
                <input
                  type="tel"
                  id="customerPhone"
                  className="form-input"
                  placeholder="Enter your phone number"
                  value={customerPhone}
                  onChange={(e) => setCustomerPhone(e.target.value)}
                  required
                />
              </div>
            </div>
          </div>
        </section>

        <section className="user-notes-section">
          <div className="container">
            <h2 className="section-title">Your Intention (Optional)</h2>
            <textarea
              className="notes-textarea"
              placeholder="Share any focus areas, intentions, or notes you'd like us to know about..."
              value={userNotes}
              onChange={(e) => setUserNotes(e.target.value)}
              rows="4"
            />
          </div>
        </section>

        {showPayPal && !paymentApproved && (
          <section className="payment-section">
            <div className="container">
              <h2 className="section-title">Complete Payment</h2>
              <div className="payment-summary">
                <div className="payment-summary-row">
                  <span className="payment-label">Specialist:</span>
                  <span className="payment-value">{specialist.name}</span>
                </div>
                <div className="payment-summary-row">
                  <span className="payment-label">Date & Time:</span>
                  <span className="payment-value">{selectedDate && formatDate(selectedDate)} at {selectedTime}</span>
                </div>
                <div className="payment-summary-row payment-total">
                  <span className="payment-label">Total Amount:</span>
                  <span className="payment-value-total">{consultationPrice}</span>
                </div>
              </div>
              <div className="paypal-container">
                {isPending && (
                  <div className="paypal-loading">
                    <div className="loading-spinner" style={{ 
                      width: '40px', 
                      height: '40px', 
                      border: '4px solid rgba(124, 58, 237, 0.3)',
                      borderTopColor: '#7C3AED',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite',
                      margin: '0 auto 1rem'
                    }}></div>
                    <p>Loading PayPal...</p>
                  </div>
                )}
                {isRejected && (
                  <div className="paypal-error">
                    <p style={{ color: '#ef4444', marginBottom: '1rem' }}>
                      Failed to load PayPal. Please refresh the page or try again later.
                    </p>
                    <button
                      type="button"
                      onClick={() => window.location.reload()}
                      className="btn-retry-paypal"
                    >
                      Retry
                    </button>
                  </div>
                )}
                {isResolved && (
                  <PayPalButtons
                    createOrder={(data, actions) => {
                      const price = extractPrice(consultationPrice);
                      if (price <= 0) {
                        setError('Invalid price amount');
                        return;
                      }
                      return actions.order.create({
                        purchase_units: [{
                          description: `Consultation with ${specialist.name}`,
                          amount: {
                            value: price.toFixed(2),
                            currency_code: "USD"
                          }
                        }],
                        application_context: {
                          brand_name: "ManaWave",
                          landing_page: "BILLING",
                          user_action: "PAY_NOW"
                        },
                        intent: "CAPTURE"
                      });
                    }}
                    onApprove={handlePaymentApprove}
                    onError={handlePaymentError}
                    onCancel={handlePaymentCancel}
                    style={{
                      layout: "vertical",
                      color: "blue",
                      shape: "rect",
                      label: "paypal"
                    }}
                  />
                )}
              </div>
              <button
                type="button"
                className="btn-cancel-payment"
                onClick={() => {
                  setShowPayPal(false);
                  setError('');
                }}
              >
                Cancel Payment
              </button>
            </div>
          </section>
        )}

        {!showPayPal && (
          <section className="confirmation-notice-section">
            <div className="container">
              <div className="notice-content">
                <div className="notice-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM13 17H11V15H13V17ZM13 13H11V7H13V13Z" fill="currentColor"/>
                  </svg>
                </div>
                <div className="notice-text">
                  <p className="notice-main">
                    Complete your booking by proceeding to secure payment. Your session will be confirmed upon successful payment.
                  </p>
                </div>
              </div>
            </div>
          </section>
        )}

        {!showPayPal && (
          <section className="cta-section">
            <div className="container">
              <button 
                type="submit"
                className={`btn-request-booking ${!isFormValid || isSubmitting ? 'disabled' : ''}`}
                disabled={!isFormValid || isSubmitting}
              >
                {isSubmitting ? 'Processing...' : 'Proceed to Payment'}
              </button>
            </div>
          </section>
        )}

        {paymentApproved && (
          <section className="cta-section">
            <div className="container">
              <div className="processing-message">
                <div className="loading-spinner" style={{ 
                  width: '40px', 
                  height: '40px', 
                  border: '4px solid rgba(124, 58, 237, 0.3)',
                  borderTopColor: '#7C3AED',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                  margin: '0 auto 1rem'
                }}></div>
                <p>Payment successful! Confirming your booking...</p>
              </div>
            </div>
          </section>
        )}

        <section className="trust-indicators-section">
          <div className="container">
            <p className="trust-text">
              Your information is kept private and secure. All sessions are conducted with 
              respect, care, and professional energy work practices.
            </p>
          </div>
        </section>
      </form>

      <footer className="book-session-footer">
        <div className="wave-footer-closure"></div>
      </footer>
    </div>
  );
}

export default BookSpecialist;
